import { inject, Injectable, Injector, Type } from '@angular/core';
import { Router } from '@angular/router';
import { LoginResponse, OidcSecurityService } from 'angular-auth-oidc-client';
import {
  Observable,
  catchError,
  distinctUntilChanged,
  filter,
  from,
  map,
  of,
  pairwise,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { HlmDialogService } from '@spartan-ng/helm/dialog';
import { TranslateService } from '@ngx-translate/core';
import { toast } from 'ngx-sonner';

import { UrlFlagsService } from '../url-flags/url-flags.service';
import { OidcEncryptedStorageService } from '../oidc-encryption/oidc-encrypted-storage.service';
import { EncryptedStorageService } from '../encryption/encrypted-storage.service';
import { AuthRedirectService } from '../../../data-access/auth/auth-redirect.service';
import { AuthStoreService } from '../../../data-access/auth/auth-store.service';
import { BlockUiService } from '../block-ui/block-ui.service';
import { EventBusService } from '../event-bus/event-bus.service';
import { UserEventBus } from '@web-portal/domains/shared/event-bus';
import { UserMapper } from '@web-portal/domains/user/anti-corruption-layer';
import { BIMPLUS_API } from '@web-portal/domains/shared/bimplus-core';
import type { Api } from 'bimplus-websdk';
import { AuthorizationStrategy } from '../../strategies';
import { ROUTE } from '../../constants/route';

@Injectable({
  providedIn: 'root',
})
export class AuthorizationService extends AuthorizationStrategy {
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly encryptedStorage = inject(EncryptedStorageService);
  private readonly dialogService = inject(HlmDialogService);
  private readonly oidcStorage = inject(OidcEncryptedStorageService);
  private readonly authStoreService = inject(AuthStoreService);
  private readonly authRedirectService = inject(AuthRedirectService);
  private readonly urlFlagsService = inject(UrlFlagsService);
  private readonly translateService = inject(TranslateService);
  private readonly blockUiService = inject(BlockUiService);
  private readonly eventBusService = inject(EventBusService);
  private readonly userEventBus = inject(UserEventBus);
  private readonly injector = inject(Injector);
  private readonly router = inject(Router);
  private loggingOut = false;

  constructor() {
    super();
    this.oidcSecurityService.userData$.subscribe(({ userData }) => {
      this.userEventBus.emit({
        type: '[User] initialized',
        payload: UserMapper.mapToUser(userData),
      });
    });

    // A failed silent renew clears the OIDC session; without this the user stays in the app with a blank profile.
    this.oidcSecurityService.isAuthenticated$
      .pipe(
        map(({ isAuthenticated }) => isAuthenticated),
        distinctUntilChanged(),
        pairwise(),
        filter(([wasAuthenticated, isAuthenticated]) => wasAuthenticated && !isAuthenticated)
      )
      .subscribe(() => this.handleSessionLost());
  }

  private handleSessionLost(): void {
    if (this.loggingOut || this.urlFlagsService.embedded()) {
      return;
    }

    this.authStoreService.resetSession();

    if (this.isLoginRoute(this.router.url)) {
      return;
    }

    void this.router.navigate(['/', ROUTE.login], {
      queryParams: { returnUrl: this.router.url },
    });
  }

  login(returnUrl?: string): void {
    const currentUrl = returnUrl ?? this.getCurrentAppUrl();

    if (currentUrl && !this.isLoginRoute(currentUrl)) {
      this.authRedirectService.save(currentUrl);
    }

    this.oidcSecurityService.authorize();
  }

  private getCurrentAppUrl(): string {
    return `${globalThis.location.pathname}${globalThis.location.search}${globalThis.location.hash}`;
  }

  private isLoginRoute(url: string): boolean {
    try {
      const parsed = new URL(url, globalThis.location.origin);
      return parsed.pathname === '/login';
    } catch {
      return url.startsWith('/login');
    }
  }

  /**
   * Logout and lock encrypted storage.
   * Ensures all pending data is saved before clearing encryption keys.
   */
  logout(): Observable<unknown> {
    this.loggingOut = true;
    return from(this.encryptedStorage.lock()).pipe(
      switchMap(() => this.oidcSecurityService.logoff())
    );
  }

  isUnauthorized(modal?: Type<unknown> | null): void {
    if (!modal) {
      return;
    }
    const dialogRef = this.dialogService.open(modal, {
      contentClass: 'border border-[var(--corporate-popover-border-color)]',
    });

    dialogRef?.closed$.subscribe(() => {
      if (this.urlFlagsService.embedded()) {
        this.blockUiService.unblock();
        toast.info(this.translateService.instant('auth.authorization-pending') + '...', {
          description: this.translateService.instant('auth.authorization-pending-please-wait'),
          duration: 15000,
        });
        this.eventBusService.emit({ type: 'requestAuthentication', payload: null });
        return;
      }
      this.login();
    });
  }

  /**
   * Check auth and initialize encrypted storage on successful authentication.
   * Uses user's email as the storage namespace and derives encryption key from user ID + app secret.
   */
  checkAuthIncludingServer(): Observable<LoginResponse> {
    // Ensure OIDC storage is ready before checking auth (so the OIDC library can read stored tokens)
    return from(this.oidcStorage.whenReady()).pipe(
      switchMap(() => this.oidcSecurityService.checkAuthIncludingServer()),
      catchError(() => of(this.unauthenticatedResponse())),
      // Stale/foreign session: authenticated without user data would leave a blank profile.
      map(response =>
        response.isAuthenticated && !response.userData ? this.unauthenticatedResponse() : response
      ),
      tap(response => {
        this.authStoreService.updateAccessToken(response.accessToken);
      }),
      switchMap(response =>
        from(this.fetchBimplusUserId(response.accessToken)).pipe(
          tap(userId => this.authStoreService.updateBimplusUserId(userId)),
          map(() => response),
          catchError((error: unknown) => {
            if (!this.isUnauthorizedError(error)) {
              return throwError(() => error);
            }
            return of(this.unauthenticatedResponse());
          })
        )
      ),
      switchMap(response => {
        if (response.isAuthenticated && response.userData) {
          return from(this.encryptedStorage.initializeStorageForUser(response)).pipe(
            switchMap(() => [response])
          );
        }
        return [response];
      })
    );
  }

  private unauthenticatedResponse(): LoginResponse {
    this.authStoreService.resetSession();
    return {
      isAuthenticated: false,
      userData: null,
      accessToken: '',
      idToken: '',
      configId: null,
    } as unknown as LoginResponse;
  }

  private async fetchBimplusUserId(accessToken: string): Promise<string> {
    const bimplusApi = this.injector.get<Api | null>(BIMPLUS_API, null);

    if (!accessToken || !bimplusApi) {
      return '';
    }

    try {
      const user = await bimplusApi.user.get(accessToken);
      return user?.id ?? '';
    } catch (error) {
      if (this.isUnauthorizedError(error)) {
        throw error;
      }
      return '';
    }
  }

  private isUnauthorizedError(error: unknown): boolean {
    return (error as { status?: number } | null)?.status === 401;
  }
}
